/**
 * saas-client.ts — Typed client for the ɳSentry SaaS REST API (/v1)
 *
 * Purpose:    Single fetch surface for Monitors/Incidents/Status Pages/Overview/
 *             API Keys/Billing. The gateway speaks the documented /v1 contract
 *             (enveloped snake_case — the same shape the CLI consumes);
 *             saas-mapping.ts converts wire payloads to these view models.
 *             Falls back to the in-browser mock (saas-mock.ts) ONLY when
 *             VITE_API_MOCK==='1' or on a genuine network/offline failure.
 * Inputs:     Base URL from resolveApiBase (api-base.ts): same-origin ('') in
 *             every prod build, whatever VITE_API_URL says (the Vercel /v1
 *             rewrite proxies to api.sentry.nself.org: no CORS, httpOnly
 *             nsentry_auth_token cookie sent); dev: VITE_API_URL, else absolute.
 * Outputs:    Result<T, SentryError> per endpoint (see ./result, ./sentry-error)
 * Constraints: authed calls send the session cookie (same-origin, prod) AND
 *              attach `Authorization: Bearer <session JWT>` when one is held
 *              (lib/saas-token, hydrated by /api/auth/session); the gateway
 *              accepts either. A 401 surfaces as an auth error (re-login),
 *              never a silent mock fallback; the mock only kicks in on a
 *              genuine network/offline failure. Public status-page lookup is
 *              the one unauthenticated endpoint.
 * SOT:        nsentry SaaS plan §API contract (task-provided authoritative shape)
 */
import { ok, err, type Result } from './result'
import { errorFromResponse, errorFromException, type SentryError } from './sentry-error'
import { mockApi } from './saas-mock'
import {
  mapApiKey,
  mapApiKeys,
  mapBilling,
  mapIncident,
  mapIncidents,
  mapMonitor,
  mapMonitorChecks,
  mapMonitors,
  mapOverview,
  mapPublicStatusPage,
  mapStatusPage,
  mapStatusPages,
  mapSubscribers,
  mapUsage,
  monitorKindFromUrl,
  statusPagePatchToWire,
  type WireApiKey,
  type WireBilling,
  type WireIncident,
  type WireMonitor,
  type WireMonitorCheck,
  type WireOverview,
  type WirePublicStatusPage,
  type WireQuota,
  type WireStatusPage,
  type WireSubscriber,
} from './saas-mapping'
import { getSaasToken } from './saas-token'
import { resolveApiBase } from './api-base'
import type { TierId } from './tiers'

// Same-origin ('') in a prod build: the Vercel /v1 rewrite proxies to
// api.sentry.nself.org, so requests carry the httpOnly session cookie and skip
// CORS. A prod build is ALWAYS same-origin (VITE_API_URL ignored, api-base.ts,
// D-0898); dev: VITE_API_URL overrides, else the absolute origin (header auth).
const API_BASE = resolveApiBase({ PROD: import.meta.env.PROD, VITE_API_URL: import.meta.env.VITE_API_URL })
export const MOCK_MODE = import.meta.env.VITE_API_MOCK === '1'

/**
 * The offline demo mock may substitute for a network failure ONLY outside a
 * production build (dev/preview/tests). In prod a network failure surfaces as
 * a typed error → AsyncScreen renders a graceful error/empty state with retry;
 * demo data must never masquerade as live tenant data.
 */
const ALLOW_OFFLINE_MOCK = MOCK_MODE || !import.meta.env.PROD

/**
 * Name of the DOM event fired when an authed /v1 call returns 401 — the
 * session is gone (expired/revoked). AuthProvider listens and clears the
 * user, which makes RequireAuth redirect to /app/login.
 */
export const AUTH_EXPIRED_EVENT = 'nsentry:auth-expired'

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Monitor {
  id: string
  name: string
  url: string
  intervalSeconds: number
  status: 'up' | 'down' | 'degraded' | 'paused' | 'pending'
  latencyMs: number | null
  lastCheckedAt: string | null
  /** ISO date the monitor's TLS certificate expires; null when unknown. */
  sslExpiresAt: string | null
}

export interface MonitorCheck {
  checkedAt: string
  status: 'up' | 'down'
  latencyMs: number | null
}

export interface Incident {
  id: string
  title: string
  status: 'open' | 'acknowledged' | 'resolved'
  severity: 'critical' | 'major' | 'minor'
  monitorId?: string
  startedAt: string
  resolvedAt?: string
}

/** One entry in an incident's timeline (GET /v1/incidents/{id}/timeline). */
export interface IncidentEvent {
  at: string
  kind: string
  message: string
}

export interface StatusPageComponent {
  id: string
  name: string
  status: 'operational' | 'degraded' | 'down'
}

export interface StatusPage {
  id: string
  slug: string
  title: string
  components: StatusPageComponent[]
  published: boolean
  /** Custom domain (paid tiers); null/empty when unset. */
  customDomain: string | null
}

/** An email subscriber to a status page's incident notifications. */
export interface StatusPageSubscriber {
  id: string
  email: string
  createdAt: string | null
}

export interface PublicStatusComponent {
  id: string
  name: string
  status: 'operational' | 'degraded' | 'down' | 'unknown'
  /** Recent uptime %, null before any probe has completed. */
  uptimePercent: number | null
}

export interface PublicStatusPage {
  title: string
  overallStatus: 'operational' | 'degraded' | 'down'
  components: PublicStatusComponent[]
  incidents: Array<{ title: string; status: string; startedAt: string }>
  generatedAt?: string
}

export interface Overview {
  monitors: {
    total: number
    up: number
    down: number
    paused: number
    pending: number
  }
  incidentsOpen: number
  /** Recent uptime percentage; null before any probe has run. */
  uptimePct24h: number | null
  errors: {
    eventsThisMonth: number
    topGroups: Array<{ message: string; count: number; lastSeen: string }>
  }
  rum: {
    pageviewsThisMonth: number
    p75LcpMs: number
    p75InpMs: number
    errorRatePct: number
  }
}

export interface ApiKey {
  id: string
  name: string
  prefix: string
  scope: 'read' | 'full'
  createdAt: string
  lastUsedAt?: string | null
  revoked?: boolean
}

export interface BillingInfo {
  tier: TierId
  usage: {
    monitors: number
    statusPages: number
    errorEventsThisMonth: number
    rumPageviewsThisMonth: number
    heartbeats: number
  }
  /** Stripe customer-portal link (stub → public billing page for now). */
  portalUrl?: string
}

// ─── Fetch helper ────────────────────────────────────────────────────────────

export async function request<T>(
  path: string,
  init?: RequestInit,
  opts?: { auth?: boolean },
): Promise<Result<T, SentryError>> {
  try {
    const authed = opts?.auth !== false
    const token = authed ? getSaasToken() : null
    const res = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
      credentials: authed ? 'include' : 'omit',
      signal: AbortSignal.timeout(8000),
    })
    if (!res.ok) {
      // Session expired/revoked on an authed call → tell AuthProvider so the
      // route guard sends the user back to /app/login (spec: 401 → re-login).
      if (res.status === 401 && authed && typeof window !== 'undefined') {
        window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT))
      }
      return err(errorFromResponse(res))
    }
    const json = (await res.json().catch(() => ({}))) as T
    return ok(json)
  } catch (e) {
    return err(errorFromException(e))
  }
}

/** True for network-shaped failures where the mock fallback should kick in. */
function isNetworkFailure(result: Result<unknown, SentryError>): boolean {
  return !result.ok && (result.error.type === 'network' || result.error.type === 'plugin_offline')
}

export async function withMockFallback<T>(
  live: () => Promise<Result<T, SentryError>>,
  mock: () => Result<T, SentryError>,
): Promise<Result<T, SentryError>> {
  if (MOCK_MODE) return mock()
  const result = await live()
  if (isNetworkFailure(result) && ALLOW_OFFLINE_MOCK) return mock()
  return result
}

/** Map a Result's success value, passing errors through untouched. */
export function mapResult<A, B>(
  r: Result<A, SentryError>,
  fn: (value: A) => B,
): Result<B, SentryError> {
  return r.ok ? ok(fn(r.value)) : r
}

// ─── Monitors ────────────────────────────────────────────────────────────────

export const monitorsApi = {
  list: () =>
    withMockFallback(
      async () => mapResult(await request<{ monitors?: WireMonitor[] }>('/v1/monitors'), mapMonitors),
      () => mockApi.listMonitors(),
    ),
  create: (input: { name: string; url: string; intervalSeconds: number }) =>
    withMockFallback(
      async () =>
        mapResult(
          await request<{ monitor: WireMonitor }>('/v1/monitors', {
            method: 'POST',
            body: JSON.stringify({
              name: input.name,
              url: input.url,
              kind: monitorKindFromUrl(input.url),
              interval_seconds: input.intervalSeconds,
            }),
          }),
          (body) => mapMonitor(body.monitor),
        ),
      () => mockApi.createMonitor(input),
    ),
  // The gateway has no monitor PATCH yet — pause/resume are the supported
  // transitions. A live PATCH surfaces the gateway's error, never mock data.
  update: (id: string, patch: Partial<Monitor>) =>
    withMockFallback(
      async () =>
        mapResult(
          await request<{ monitor: WireMonitor }>(`/v1/monitors/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
          }),
          (body) => mapMonitor(body.monitor),
        ),
      () => mockApi.updateMonitor(id, patch),
    ),
  remove: (id: string) =>
    withMockFallback(
      () => request<void>(`/v1/monitors/${id}`, { method: 'DELETE' }),
      () => mockApi.deleteMonitor(id),
    ),
  /** Pause/resume via the gateway's PATCH {paused} (POST pause/resume also exists). */
  setPaused: (id: string, paused: boolean) =>
    withMockFallback(
      async () =>
        mapResult(
          await request<{ monitor: WireMonitor }>(`/v1/monitors/${id}`, {
            method: 'PATCH',
            body: JSON.stringify({ paused }),
          }),
          (body) => mapMonitor(body.monitor),
        ),
      () => mockApi.updateMonitor(id, { status: paused ? 'paused' : 'pending' }),
    ),
  /**
   * Per-monitor probe history for the detail page's uptime chart. The
   * gateway route is rolling out — callers treat 404 as "coming online"
   * (isComingOnline) rather than an error.
   */
  checks: (id: string, limit = 60) =>
    withMockFallback(
      async () =>
        mapResult(
          await request<{ checks?: WireMonitorCheck[] }>(
            `/v1/monitors/${id}/checks?limit=${limit}`,
          ),
          mapMonitorChecks,
        ),
      () => mockApi.monitorChecks(id),
    ),
}

// ─── Incidents ───────────────────────────────────────────────────────────────

export const incidentsApi = {
  list: () =>
    withMockFallback(
      async () => mapResult(await request<{ incidents?: WireIncident[] }>('/v1/incidents'), mapIncidents),
      () => mockApi.listIncidents(),
    ),
  create: (input: { title: string; severity: Incident['severity']; monitorId?: string }) =>
    withMockFallback(
      async () =>
        mapResult(
          await request<{ incident: WireIncident }>('/v1/incidents', {
            method: 'POST',
            body: JSON.stringify({
              title: input.title,
              severity: input.severity,
              ...(input.monitorId ? { monitor_id: input.monitorId } : {}),
            }),
          }),
          (body) => mapIncident(body.incident),
        ),
      () => mockApi.createIncident(input),
    ),
  /**
   * Incident timeline. The gateway route is rolling out — callers treat
   * 404/405/501 as "coming online" (isComingOnline), not an error.
   */
  timeline: (id: string) =>
    withMockFallback(
      async () =>
        mapResult(
          await request<{ events?: Array<{ at: string; kind: string; message: string }> }>(
            `/v1/incidents/${id}/timeline`,
          ),
          (body) => (body.events ?? []) as IncidentEvent[],
        ),
      () => mockApi.incidentTimeline(id),
    ),
  ack: (id: string) =>
    withMockFallback(
      async () =>
        mapResult(
          await request<{ incident: WireIncident }>(`/v1/incidents/${id}/ack`, { method: 'POST' }),
          (body) => mapIncident(body.incident),
        ),
      () => mockApi.ackIncident(id),
    ),
  resolve: (id: string) =>
    withMockFallback(
      async () =>
        mapResult(
          await request<{ incident: WireIncident }>(`/v1/incidents/${id}/resolve`, { method: 'POST' }),
          (body) => mapIncident(body.incident),
        ),
      () => mockApi.resolveIncident(id),
    ),
}

// ─── Status pages ────────────────────────────────────────────────────────────

export const statusPagesApi = {
  list: () =>
    withMockFallback(
      async () =>
        mapResult(await request<{ status_pages?: WireStatusPage[] }>('/v1/status-pages'), mapStatusPages),
      () => mockApi.listStatusPages(),
    ),
  create: (input: { slug: string; title: string }) =>
    withMockFallback(
      async () =>
        mapResult(
          await request<{ status_page: WireStatusPage }>('/v1/status-pages', {
            method: 'POST',
            // Gateway contract: {"name","slug"} — the SPA's "title" is the name.
            body: JSON.stringify({ name: input.title, slug: input.slug }),
          }),
          (body) => mapStatusPage(body.status_page),
        ),
      () => mockApi.createStatusPage(input),
    ),
  update: (id: string, patch: Partial<StatusPage>) =>
    withMockFallback(
      async () =>
        mapResult(
          await request<{ status_page: WireStatusPage }>(`/v1/status-pages/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(statusPagePatchToWire(patch)),
          }),
          (body) => mapStatusPage(body.status_page),
        ),
      () => mockApi.updateStatusPage(id, patch),
    ),
  /**
   * Replace a page's component list (POST /v1/status-pages/{id}/components —
   * documented, rolling out). Callers fall back to update(id, {components})
   * when this returns coming-online.
   */
  setComponents: (id: string, components: StatusPageComponent[]) =>
    withMockFallback(
      () =>
        request<void>(`/v1/status-pages/${id}/components`, {
          method: 'POST',
          body: JSON.stringify({
            components: components.map((c) => ({ id: c.id, name: c.name, status: c.status })),
          }),
        }),
      () => mapResult(mockApi.updateStatusPage(id, { components }), () => undefined),
    ),
  remove: (id: string) =>
    withMockFallback(
      () => request<void>(`/v1/status-pages/${id}`, { method: 'DELETE' }),
      () => mockApi.deleteStatusPage(id),
    ),
  /** Email subscribers (incident notifications) for one status page. */
  subscribers: (pageId: string) =>
    withMockFallback(
      async () =>
        mapResult(
          await request<{ subscribers?: WireSubscriber[] }>(
            `/v1/subscribers?status_page_id=${encodeURIComponent(pageId)}`,
          ),
          mapSubscribers,
        ),
      () => mockApi.listSubscribers(pageId),
    ),
  addSubscriber: (pageId: string, email: string) =>
    withMockFallback(
      async () =>
        mapResult(
          await request<{ subscriber?: WireSubscriber }>('/v1/subscribers', {
            method: 'POST',
            body: JSON.stringify({ status_page_id: pageId, email }),
          }),
          (body) => mapSubscribers({ subscribers: body.subscriber ? [body.subscriber] : [] })[0],
        ),
      () => mockApi.addSubscriber(pageId, email),
    ),
  removeSubscriber: (id: string) =>
    withMockFallback(
      () => request<void>(`/v1/subscribers/${id}`, { method: 'DELETE' }),
      () => mockApi.removeSubscriber(id),
    ),
  public: (slug: string) =>
    withMockFallback(
      async () =>
        mapResult(
          await request<{ status_page?: WirePublicStatusPage }>(
            `/v1/status/public/${encodeURIComponent(slug)}`,
            undefined,
            { auth: false },
          ),
          mapPublicStatusPage,
        ),
      () => mockApi.publicStatusPage(slug),
    ),
}

// ─── Overview ────────────────────────────────────────────────────────────────

export const overviewApi = {
  get: () =>
    withMockFallback(
      async () => mapResult(await request<{ overview?: WireOverview }>('/v1/overview'), mapOverview),
      () => mockApi.overview(),
    ),
}

// ─── API keys ────────────────────────────────────────────────────────────────

export const apiKeysApi = {
  list: () =>
    withMockFallback(
      async () => mapResult(await request<{ api_keys?: WireApiKey[] }>('/v1/api-keys'), mapApiKeys),
      () => mockApi.listApiKeys(),
    ),
  create: (input: { name: string; scope: 'read' | 'full' }) =>
    withMockFallback(
      async () =>
        mapResult(
          await request<{ api_key: WireApiKey; key: string }>('/v1/api-keys', {
            method: 'POST',
            body: JSON.stringify(input),
          }),
          (body) => ({ ...mapApiKey(body.api_key), key: body.key }),
        ),
      () => mockApi.createApiKey(input),
    ),
  remove: (id: string) =>
    withMockFallback(
      () => request<void>(`/v1/api-keys/${id}`, { method: 'DELETE' }),
      () => mockApi.revokeApiKey(id),
    ),
}

// ─── Billing ─────────────────────────────────────────────────────────────────

export const billingApi = {
  get: () =>
    withMockFallback(
      async () => mapResult(await request<{ billing?: WireBilling }>('/v1/billing'), mapBilling),
      () => mockApi.billing(),
    ),
  checkout: (input: { plan: 'sentry-bundle' | 'nself-plus'; interval: 'monthly' | 'annual' }) =>
    withMockFallback(
      () => request<{ url: string }>('/v1/billing/checkout', { method: 'POST', body: JSON.stringify(input) }),
      () => mockApi.checkout(),
    ),
  /**
   * Stripe customer-portal session. POST /v1/billing/portal mints a
   * per-customer session; while that route is rolling out, callers fall
   * back to BillingInfo.portalUrl from GET /v1/billing.
   */
  portal: () =>
    withMockFallback(
      () => request<{ url: string }>('/v1/billing/portal', { method: 'POST' }),
      () => mockApi.checkout(),
    ),
  /**
   * GET /v1/usage — dedicated usage endpoint (shipping with the gateway this
   * wave). Callers that already hold GET /v1/billing usage may treat a
   * coming-online result as "keep the billing envelope's numbers".
   */
  usage: () =>
    withMockFallback(
      async () =>
        mapResult(await request<{ usage?: Record<string, WireQuota> }>('/v1/usage'), mapUsage),
      () => mapResult(mockApi.billing(), (b) => b.usage),
    ),
}
